// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #139 Phase 8.3 — Region-pill selection state + toggle helpers.
//
// Regions are NOT a persisted filter — they're a shortcut for adjusting
// the persisted `Users.dashboardLocations` array. A region pill's visual
// state is DERIVED from the current dashboardLocations + that region's
// location codes:
//
//   "all"  → every location in this region is in dashboardLocations
//   "some" → at least one but not all are in (indeterminate state)
//   "none" → no location from this region is in dashboardLocations
//
// Clicking a region TOGGLES — if state is "all", remove all its locations
// from the selection; otherwise (some/none), add all its locations
// (set union). Manually adjusting individual locations on top of a
// region pick is preserved — regions don't lock anything.
//
// All functions here are pure so they're trivially unit-testable.

export const REGION_STATE_NONE = "none";
export const REGION_STATE_SOME = "some";
export const REGION_STATE_ALL = "all";

/**
 * Returns "all" | "some" | "none" describing how much of the given
 * region's locations are currently in the user's dashboardLocations
 * selection.
 */
export function getRegionSelectionState(regionLocations, currentSelection) {
  if (!Array.isArray(regionLocations) || regionLocations.length === 0) {
    return REGION_STATE_NONE;
  }
  const selSet = new Set(
    Array.isArray(currentSelection) ? currentSelection : []
  );
  let count = 0;
  for (const loc of regionLocations) {
    if (selSet.has(loc)) count += 1;
  }
  if (count === 0) return REGION_STATE_NONE;
  if (count === regionLocations.length) return REGION_STATE_ALL;
  return REGION_STATE_SOME;
}

/**
 * Returns the next dashboardLocations array after toggling a region:
 *   - "all" → remove every location in the region from the selection
 *   - "some" or "none" → add every location in the region to the selection
 *
 * Locations NOT in this region are left untouched. Order is preserved
 * for any locations that survive the toggle; newly-added locations are
 * appended in the order they appear in regionLocations.
 */
export function toggleRegionInSelection(regionLocations, currentSelection) {
  if (!Array.isArray(regionLocations) || regionLocations.length === 0) {
    return Array.isArray(currentSelection) ? currentSelection.slice() : [];
  }
  const current = Array.isArray(currentSelection)
    ? currentSelection.slice()
    : [];
  const state = getRegionSelectionState(regionLocations, current);
  const regionSet = new Set(regionLocations);

  if (state === REGION_STATE_ALL) {
    // Remove the region's locations
    return current.filter((loc) => !regionSet.has(loc));
  }

  // Add the region's locations (union, no duplicates, append-after-existing
  // so existing manual selections retain their position)
  const currentSet = new Set(current);
  const next = current.slice();
  for (const loc of regionLocations) {
    if (!currentSet.has(loc)) next.push(loc);
  }
  return next;
}

// - - - - - - - - - - all-regions toggle ("Check all regions" / "Uncheck all regions")

// Collapse the regionList (Array<{ region, locations }>) into a flat
// list of every region-affiliated location code. Used by the
// "all regions" state + toggle helpers.
function flattenRegionLocations(regionList) {
  if (!Array.isArray(regionList)) return [];
  const all = [];
  for (const entry of regionList) {
    if (!entry || !Array.isArray(entry.locations)) continue;
    for (const loc of entry.locations) {
      if (!all.includes(loc)) all.push(loc);
    }
  }
  return all;
}

/**
 * Aggregate state across every region in the list.
 *   "all"  → every region's locations are all selected
 *   "some" → some are selected (at least one location anywhere is selected)
 *   "none" → no region's locations are selected at all
 *
 * Accepts the array-of-tuples shape returned by buildRegionLocationMap.
 */
export function getAllRegionsSelectionState(regionList, currentSelection) {
  const allLocations = flattenRegionLocations(regionList);
  return getRegionSelectionState(allLocations, currentSelection);
}

/**
 * Toggle ALL regions' locations at once:
 *   - if every region is fully selected → remove all region-affiliated
 *     locations from the selection (non-region locations stay)
 *   - else → add every region's locations to the selection
 *
 * The "non-region locations stay" property matters because some offices
 * may not yet have a region field (during the bulk-push window). Those
 * locations remain manually selectable and unaffected by the regions
 * toggle.
 *
 * Accepts the array-of-tuples shape returned by buildRegionLocationMap.
 */
export function toggleAllRegionsInSelection(regionList, currentSelection) {
  const allLocations = flattenRegionLocations(regionList);
  if (allLocations.length === 0) {
    return Array.isArray(currentSelection) ? currentSelection.slice() : [];
  }
  return toggleRegionInSelection(allLocations, currentSelection);
}
